import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const IDLE_WARNING_MS = 8 * 60 * 1000;   // 8 minutes until warning
const COUNTDOWN_MS = 2 * 60 * 1000;     // 2 minutes countdown (total session = 10 min)
const COUNTDOWN_SECONDS = Math.floor(COUNTDOWN_MS / 1000);

const EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

const SessionIdleContext = createContext(null);

export const useSessionIdle = () => useContext(SessionIdleContext);

export const SessionIdleProvider = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(COUNTDOWN_SECONDS);
  const idleTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const resetIdleTimer = useCallback(() => {
    if (!user || showWarning) return;
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdownSeconds(COUNTDOWN_SECONDS);
    }, IDLE_WARNING_MS);
  }, [user, showWarning]);

  const stopCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const handleStayLoggedIn = useCallback(() => {
    stopCountdown();
    setShowWarning(false);
    setCountdownSeconds(COUNTDOWN_SECONDS);
    // Idle timer is restarted by the effect when showWarning becomes false (resetIdleTimer updates)
  }, [stopCountdown]);

  const handleLogout = useCallback(() => {
    stopCountdown();
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    setShowWarning(false);
    logout();
    navigate('/login');
  }, [logout, navigate, stopCountdown]);

  // Start idle timer when user is logged in; reset on activity
  useEffect(() => {
    if (!user) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
      stopCountdown();
      setShowWarning(false);
      return;
    }
    resetIdleTimer();
    const onActivity = () => resetIdleTimer();
    EVENTS.forEach((ev) => window.addEventListener(ev, onActivity));
    return () => {
      EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, resetIdleTimer, stopCountdown]);

  // Countdown when warning is shown
  useEffect(() => {
    if (!showWarning) return;
    countdownIntervalRef.current = setInterval(() => {
      setCountdownSeconds((s) => {
        if (s <= 1) {
          stopCountdown();
          handleLogout();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => stopCountdown();
  }, [showWarning, handleLogout, stopCountdown]);

  const formatCountdown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m === 0) return `${s} second${s !== 1 ? 's' : ''}`;
    return `${m} minute${m !== 1 ? 's' : ''} and ${s} second${s !== 1 ? 's' : ''}`;
  };

  return (
    <SessionIdleContext.Provider value={{ showWarning, countdownSeconds, handleStayLoggedIn, handleLogout }}>
      {children}
      {user && (
        <Dialog open={showWarning} onOpenChange={(open) => !open && handleLogout()}>
          <DialogContent className="sm:max-w-md rounded-2xl shadow-xl border-slate-200" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#0F172A]">
                Are you still there?
              </DialogTitle>
              <DialogDescription className="text-slate-600 pt-2">
                We notice you've been away for a while. To protect your account, you'll be logged out in <strong className="text-[#0F172A]">{formatCountdown(countdownSeconds)}</strong>.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-row justify-end gap-3 pt-6 sm:pt-4">
              <Button
                type="button"
                variant="outline"
                className="rounded-xl border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                onClick={handleLogout}
              >
                Logout
              </Button>
              <Button
                type="button"
                className="rounded-xl bg-[#1C472B] hover:bg-[#143620] text-white font-semibold"
                onClick={handleStayLoggedIn}
              >
                Stay logged in
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </SessionIdleContext.Provider>
  );
};
